import { Music, Volume2, Mic } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { useSfx } from "@/hooks/useSfx";
import { useMusic } from "@/hooks/useMusic";
import { useInstructorVoice } from "@/hooks/useInstructorVoice";

/** Persistent audio settings card for the profile screen. */
export default function AudioSettingsCard() {
  const sfx = useSfx();
  const music = useMusic();
  const voice = useInstructorVoice();

  const reset = () => {
    music.setEnabled(true);
    music.setVolume(0.5);
    sfx.setMuted(false);
    voice.setEnabled(true);
    voice.setVolume(0.9);
  };

  return (
    <Card className="bg-card/95 backdrop-blur-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Volume2 className="h-4 w-4" /> Audio
        </CardTitle>
        <CardDescription>
          Music and sound effects play during beta learning experiences only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="profile-music" className="flex items-center gap-2 text-sm font-medium">
              <Music className="h-4 w-4" /> Music
            </label>
            <Switch id="profile-music" checked={music.enabled} onCheckedChange={music.setEnabled} />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-16 text-xs text-muted-foreground">Volume</span>
            <Slider
              aria-label="Music volume"
              value={[Math.round(music.volume * 100)]}
              min={0} max={100} step={5}
              disabled={!music.enabled}
              onValueChange={([v]) => music.setVolume((v ?? 0) / 100)}
              className="flex-1"
            />
            <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
              {Math.round(music.volume * 100)}%
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="profile-sfx" className="text-sm font-medium">Sound effects</label>
            <Switch id="profile-sfx" checked={!sfx.muted} onCheckedChange={(v) => sfx.setMuted(!v)} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="profile-voice" className="flex items-center gap-2 text-sm font-medium">
              <Mic className="h-4 w-4" /> Instructor voice
            </label>
            <Switch id="profile-voice" checked={voice.enabled} onCheckedChange={voice.setEnabled} />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-16 text-xs text-muted-foreground">Volume</span>
            <Slider
              aria-label="Instructor voice volume"
              value={[Math.round(voice.volume * 100)]}
              min={0} max={100} step={5}
              disabled={!voice.enabled}
              onValueChange={([v]) => voice.setVolume((v ?? 0) / 100)}
              className="flex-1"
            />
            <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
              {Math.round(voice.volume * 100)}%
            </span>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={reset}>Reset to defaults</Button>
        </div>
      </CardContent>
    </Card>
  );
}